import React, { useEffect, useState } from "react"
import moment from "moment"
import {
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  Icon,
  MenuItem,
  Pagination,
  Select,
} from "@mui/material"
import { pdf } from "@react-pdf/renderer"
import { useNavigate } from "react-router-dom"
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil"
import { substractDays } from "utils/functions/global"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import MDDatePicker from "components/MDDatePicker"
import MDInput from "components/MDInput"
import MDSnackbar from "components/MDSnackbar"
import translate from "utils/translate"
import PackageDataTable from "layouts/exit-voucher/DataTable/PackageDataTable"

// Service
import * as packageService from "services/packages/package.service"

// Data
import packagesDataTable from "layouts/exit-voucher/data/packagesDataTable"
import { VoucherDocument } from "./pdf/VoucherDocument"
import { translateAccountName } from "utils/translate/status"

// State management
import { exitVoucherPackagesState, exitVoucherRowIdsState } from "./recoil/states"
import useScanDetection from "use-scan-detection"

function ExitVoucher() {
  const navigate = useNavigate()
  const selectedRowIdsFromStore = useRecoilValue(exitVoucherRowIdsState)
  const [recoilPackages, setRecoilPackages] = useRecoilState(exitVoucherPackagesState)
  const resetSelectedRowIds = useResetRecoilState(exitVoucherRowIdsState)
  const [account, setAccount] = useState("-")
  const [isPrinted, setIsPrinted] = useState("-")
  const [companyName, setCompanyName] = useState(0)
  const [statusFilter, setStatusFilter] = useState("-")
  const [startDatePicker, setStartDatePicker] = useState(substractDays())
  const [endDatePicker, setEndDatePicker] = useState(moment().format("YYYY-MM-DD"))
  const [search, setSearch] = useState("")
  const [packageList, setPackageList] = useState([])
  const [backupPackageList, setBackupPackageList] = useState([])
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [packageTotal, setPackageTotal] = useState(0)
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    content: "",
    color: "primary",
    icon: "",
  })
  const [barcode, setBarcode] = useState("Pas de scan de code-barres")
  const rowsPerPage = 10

  // Handle start date picker change event
  const handleStartDatePicker = (date) => {
    const formatDate = moment(date[0]).format("YYYY-MM-DD") || ""
    setStartDatePicker(formatDate)
  }

  // Handle end date picker change event
  const handleEndDatePicker = (date) => {
    const formatDate = moment(date[0]).format("YYYY-MM-DD") || ""
    setEndDatePicker(formatDate)
  }

  // intialize start date and end date
  const intializeDates = () => {
    const startFrom = substractDays()
    const today = moment().format("YYYY-MM-DD")
    setStartDatePicker(startFrom)
    setEndDatePicker(today)
  }

  // Handle open snackbar
  const handleOpenSnackbar = (status, content, icon) => {
    setSnackbarObject({
      ...snackbarObject,
      open: true,
      content,
      color: status,
      icon,
    })
  }

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarObject({
      ...snackbarObject,
      open: false,
    })
  }

  // Handle search by client names and phone numbers
  const handleSearchChange = async (evt) => {
    try {
      const word = evt.target.value
      setSearch(word)
      if (word.length >= 3) {
        const isConfirmed = true

        const companyNameEmptyString = companyName === 0 ? "" : companyName
        const statusFilterEmptyString = statusFilter === "-" ? "" : statusFilter
        const searchPackages = await packageService.findAllPackages(
          companyNameEmptyString,
          statusFilterEmptyString,
          startDatePicker,
          endDatePicker,
          currentPage,
          word,
          isConfirmed
        )

        setPackageList(searchPackages.data.packageList)
      } else {
        setPackageList(backupPackageList)
      }
    } catch (error) {
      setPackageList(backupPackageList)
    }
  }

  // Handle account event
  const handleChangeAccount = (evt) => {
    const targetValue = evt.target.value
    setAccount(targetValue)
  }

  // Handle company event
  const handleChangeCompany = (evt) => {
    const targetValue = evt.target.value
    setCompanyName(targetValue)
  }

  // Handle printed event
  const handleChangePrinted = (evt) => {
    const targetValue = evt.target.value
    setIsPrinted(targetValue)
  }

  // Handle status event
  const handleChangeStatus = (evt) => {
    const targetValue = evt.target.value
    setStatusFilter(targetValue)
  }

  // Reset filters
  const resetFilters = () => {
    setCompanyName(0)
    setStartDatePicker("")
    setEndDatePicker("")
    setSearch("")
    setStatusFilter("-")
    setIsPrinted("-")
    setAccount("-")
    intializeDates()
    onResetRowIds()
  }

  // Reset recoil store
  const onResetRowIds = () => {
    resetSelectedRowIds()
    navigate(0)
  }

  // Gel all packages
  const getAllPackages = async () => {
    try {
      setIsDataLoading(true)
      const companyNameEmptyString = companyName === 0 ? "" : companyName
      const statusFilterEmptyString = statusFilter === "-" ? "" : statusFilter
      const isPrintedEmptyString = isPrinted === "-" ? "" : isPrinted
      const accountEmptyString = account === "-" ? "" : account

      const isConfirmed = true

      const { data } = await packageService.findAllPackages(
        companyNameEmptyString,
        statusFilterEmptyString,
        startDatePicker,
        endDatePicker,
        currentPage,
        search,
        isPrintedEmptyString,
        accountEmptyString,
        isConfirmed
      )
      setRecoilPackages((prevPackageList) => [...prevPackageList, data.packageList])
      setPackageList(data.packageList)
      setBackupPackageList(data.packageList)
      setPackageTotal(data.packageTotal)
      setIsDataLoading(false)
    } catch (error) {
      setPackageList([])
    }
  }

  // Handle pagination change
  const handleChangePagination = (event, value) => {
    setCurrentPage(value)
  }

  // Calculate number of pages
  const pageCount = (total) => {
    if (total <= rowsPerPage) return rowsPerPage
    let result = total / rowsPerPage
    if (result % 1 !== 0) result = Math.floor(result) + 1
    return result
  }

  // Prepare package list for printing
  const preparePackageList = (selectedBarcodes) => {
    const generatePackagesVoucher = []
    const flatRecoilPackages = recoilPackages.flat()
    for (let index = 0; index < selectedBarcodes.length; index++) {
      const barcodeElement = selectedBarcodes[index]
      const packageItem = flatRecoilPackages.find((item) => item.barcode === barcodeElement)
      generatePackagesVoucher.push({
        barcode: packageItem.barcode,
        clientName: packageItem.clientName,
        clientPhoneNumber: packageItem.clientPhoneNumber,
        description: packageItem.description,
        packagePrice: packageItem.packagePrice,
        state: packageItem.state,
      })
    }
    return generatePackagesVoucher
  }

  // Spread rowIds object keys
  const getRowIdsKeys = () => {
    return Object.keys(selectedRowIdsFromStore)
  }

  // Handle print exit voucher document
  const printExitVoucherDocument = async () => {
    if (companyName === 0) {
      handleOpenSnackbar("error", "Aucune entreprise sélectionnée", "error")
      return
    } else if (account === "-") {
      handleOpenSnackbar("error", "Aucun compte sélectionnée", "error")
      return
    }
    setIsLoadingPDF(true)
    try {
      const creationDate = moment().format("DD/MM/YYYY")
      const dataDocument = preparePackageList(getRowIdsKeys())
      if (getRowIdsKeys().length > 0) {
        const pdfDocument = pdf(
          <VoucherDocument
            dataDocument={dataDocument}
            companyName={companyName}
            account={translateAccountName(account)}
            creationDate={creationDate}
          />
        ).toBlob()
        pdfDocument.then((blob) => {
          const url = URL.createObjectURL(blob)
          window.open(url, "_blank")
          onResetRowIds()
        })
      }
      setIsLoadingPDF(false)
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Refresh all status
  const refreshAllStatus = async () => {
    setIsLoadingPDF(true)
    try {
      const packageInstance = await packageService.refreshStatus()
      if (packageInstance.status === 200) {
        handleOpenSnackbar("success", "Toutes les statues ont été rafraîchies", "check_circle")
        setIsLoadingPDF(false)
        navigate(0)
      }
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Refresh all status
  const refreshMissingPackages = async () => {
    setIsLoadingPDF(true)
    try {
      const packageInstance = await packageService.refreshMissingPackages()
      if (packageInstance.status === 200) {
        handleOpenSnackbar("success", "Tous les colis manquants ont été rafraichis", "check_circle")
        setIsLoadingPDF(false)
        navigate(0)
      }
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Handle row ids
  const getRowId = (row, relativeIndex, parent) => {
    const barcode = row.barcode.props.children
    return parent ? [parent.id, barcode].join(".") : barcode
  }

  useEffect(() => {
    getAllPackages()
    return () => {
      if (window.location.pathname !== "/bon-de-sortie") {
        onResetRowIds()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDatePicker, endDatePicker, companyName, currentPage, search, statusFilter, isPrinted, account])

  // this code for scan barcode with machine
  useScanDetection({
    onComplete: setBarcode,
    minLength: 3,
  })

  const updateStatusWithBarCode = async () => {
    try {
      if (barcode) {
        let payload = { barcode: barcode, status: "shipped" }

        const result = await packageService.updatePackageStatus(barcode, payload)

        if (result) {
          if (result.status === 200) {
            handleOpenSnackbar("success", "Le colis modifier vers expédié", "check_circle")
          }
        }
        if (result && result.response) {
          if (result.response.status === 400) {
            handleOpenSnackbar("error", "Le colis doit être en attente pour modifié expédié", "error")
          }
          if (result.response.status === 404) {
            handleOpenSnackbar("error", "Le colis sélectionné n'est existe pas", "error")
          }
        }
      }
    } catch (error) {
      handleOpenSnackbar("error", "erreur lors de sélectionne", "error")
    }
  }

  useEffect(() => {
    if (barcode !== "Pas de scan de code-barres") {
      updateStatusWithBarCode()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode])

  return (
    <>
      <DashboardLayout>
        <DashboardNavbar absolute />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <MDTypography mt={2} gutterBottom variant="body1">
                    Filtrage des colis
                  </MDTypography>
                  <MDBox>
                    <Grid container display="flex" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Grid item xs={12} lg={2}>
                        <MDInput fullWidth label="Cherche ici..." value={search} onChange={handleSearchChange} />
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-company_id-label"
                            id="id-filter-company_id"
                            value={companyName}
                            onChange={handleChangeCompany}
                            label="Company"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value={0}>
                              <em>Toutes les entreprises</em>
                            </MenuItem>
                            <MenuItem value="Alamana">Alamana</MenuItem>
                            <MenuItem value="Lazajella">Lazajella</MenuItem>
                            <MenuItem value="mylerz">Mylerz</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-status-label"
                            id="id-filter-status"
                            value={statusFilter}
                            onChange={handleChangeStatus}
                            label="Status"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-">
                              <em>Tous les statuts</em>
                            </MenuItem>
                            <MenuItem value="A Enlever">A Enlever</MenuItem>
                            <MenuItem value="Manquant">Manquant</MenuItem>
                            <MenuItem value="Endommagé">Endommagé</MenuItem>
                            <MenuItem value="Annuler">Annuler</MenuItem>
                            <MenuItem value="canceled_internally">Annulé En Interne</MenuItem>
                            <MenuItem value="Anomalie d`Enlévement">Anomalie d`Enlévement</MenuItem>
                            <MenuItem value="Anomalie de Livraison">Anomalie de Livraison</MenuItem>
                            <MenuItem value="Au Dépôt">Au Dépôt</MenuItem>
                            <MenuItem value="Echange Reçu">Echange Reçu</MenuItem>
                            <MenuItem value="pending">En Attente</MenuItem>
                            <MenuItem value="En Cours de Livraison">En Cours de Livraison</MenuItem>
                            <MenuItem value="EN COURS">EN COURS</MenuItem>
                            <MenuItem value="En Depot">En Depot</MenuItem>
                            <MenuItem value="Enlevé">Enlevé</MenuItem>
                            <MenuItem value="Enleve">Enleve</MenuItem>
                            <MenuItem value="En Transit">En Transit</MenuItem>
                            <MenuItem value="Livré">Livré</MenuItem>
                            <MenuItem value="Livré Payé">Livré Payé</MenuItem>
                            <MenuItem value="NON REÇUE">NON REÇUE</MenuItem>
                            <MenuItem value="Payé">Payé</MenuItem>
                            <MenuItem value="Préparation Retour">Préparation Retour</MenuItem>
                            <MenuItem value="Retour Payé">Retour Payé</MenuItem>
                            <MenuItem value="Retour">Retour</MenuItem>
                            <MenuItem value="Retour Reçu">Retour Reçu</MenuItem>
                            <MenuItem value="Retour Client Agence">Retour Client Agence</MenuItem>
                            <MenuItem value="Retour Définitif">Retour Définitif</MenuItem>
                            <MenuItem value="Retour Expéditeur">Retour Expéditeur</MenuItem>
                            <MenuItem value="Retour Dépôt">Retour Dépôt</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-printed-label"
                            id="id-filter-printed"
                            value={isPrinted}
                            onChange={handleChangePrinted}
                            label="Printed"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-">
                              <em>Tout sélectionner</em>
                            </MenuItem>
                            <MenuItem value={true}>Imprimé</MenuItem>
                            <MenuItem value={false}>Non imprimé</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-accounts-label"
                            id="id-filter-accounts"
                            value={account}
                            onChange={handleChangeAccount}
                            label="Account"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-">
                              <em>Tout sélectionner</em>
                            </MenuItem>
                            <MenuItem value="account1">{translateAccountName("account1")}</MenuItem>
                            {companyName !== "Alamana" && (
                              <MenuItem value="account2">{translateAccountName("account2")}</MenuItem>
                            )}
                            <MenuItem value="account3">{translateAccountName("account3")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={1}>
                        <MDDatePicker
                          onChange={handleStartDatePicker}
                          value={startDatePicker}
                          input={{
                            placeholder: "Sélectionner la date de début",
                            fullWidth: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} lg={1}>
                        <MDDatePicker
                          onChange={handleEndDatePicker}
                          value={endDatePicker}
                          input={{
                            placeholder: "Sélectionner la date de fin",
                            fullWidth: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} lg={3}>
              <p>Code-barres détecté: {barcode}</p>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDTypography variant="h6" color="white">
                    Gestion des colis
                  </MDTypography>
                  <MDTypography variant="h6" color="white">
                    Nombre des colis sélectionné ({Object.keys(selectedRowIdsFromStore).length})
                  </MDTypography>
                </MDBox>
                <MDBox pt={3}>
                  {isDataLoading ? (
                    <MDBox
                      p={26}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MDTypography component="p" variant="body2">
                        Loading...
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <PackageDataTable
                      table={packagesDataTable(packageList)}
                      isSorted={false}
                      noEndBorder={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      getRowId={getRowId}
                      onResetRowIds={() => onResetRowIds()}
                    />
                  )}
                  <Grid container p={3} pb={1}>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={printExitVoucherDocument}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "print"}</Icon>
                        &nbsp;imprimer bon de sortie
                      </MDButton>
                    </Grid>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={refreshAllStatus}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "refresh"}</Icon>
                        &nbsp;actualiser tous les statuts
                      </MDButton>
                    </Grid>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={refreshMissingPackages}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "hourglass_top"}</Icon>
                        &nbsp;actualiser tous les colis manquants
                      </MDButton>
                    </Grid>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={resetFilters}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "restart_alt"}</Icon>
                        &nbsp;réinitialiser tous les filtres
                      </MDButton>
                    </Grid>
                  </Grid>
                  {packageTotal > 10 && (
                    <Grid p={3} pt={0} container alignItems="center" justifyContent="space-between">
                      <Grid item sm={12} md={6} pt={3}>
                        <MDTypography component="p" variant="body2">
                          Affichage {currentPage} à {rowsPerPage} sur {packageTotal} colis
                        </MDTypography>
                      </Grid>
                      <Grid item pt={3}>
                        <Pagination
                          count={pageCount(packageTotal)}
                          defaultPage={currentPage}
                          page={currentPage}
                          onChange={handleChangePagination}
                          color="info"
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
      <MDSnackbar
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title="Bon de sortie"
        content={snackbarObject.content}
        open={snackbarObject.open}
        close={handleCloseSnackbar}
        dateTime=""
        sx={{
          float: "left",
        }}
      />
    </>
  )
}

export default ExitVoucher
